import { Injectable } from '@angular/core'
import { forkJoin, ReplaySubject } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import {
  AccountDto,
  AccountResourceTypeConfigsDto,
  EntitlementDto,
  EntitlerDto,
  IdentityProviderConfigDto,
  IdentityProviderDto,
  IntegrationConfigDto,
  IntegrationDto,
  SpotByNetAppConfigDto,
} from 'src/app/services/dtos'
import { ApiService } from '../../services/api.service'

export abstract class IntegrationConfig {
  static integrationType: string
  getIntegrationType() {
    // To pull the static type from a derived class in TypeScript, we have to
    // dynamically reference "this.constructor", and cast it as well.
    return (<typeof IntegrationConfig>this.constructor).integrationType
  }

  static typeDisplayString: string
  getTypeDisplayString() {
    // To pull the static type from a derived class in TypeScript, we have to
    // dynamically reference "this.constructor", and cast it as well.
    return (<typeof IntegrationConfig>this.constructor).typeDisplayString
  }

  abstract toDto(): IntegrationConfigDto
}

export class SpotByNetAppIntegrationConfig extends IntegrationConfig {
  static integrationType = 'spotByNetApp'
  static typeDisplayString = 'Spot by NetApp'

  constructor(
    public sourceAccountId: string,
    public sourceAuthToken: string,
    public targetAccountId: string,
    public targetAuthToken: string
  ) {
    super()
  }

  toDto(): SpotByNetAppConfigDto {
    return {
      type: SpotByNetAppIntegrationConfig.integrationType,
      sourceAccountId: this.sourceAccountId,
      sourceAuthToken: this.sourceAuthToken,
      targetAccountId: this.targetAccountId,
      targetAuthToken: this.targetAuthToken,
    }
  }
}

// This mapping is used in the integration configuration dialog to
// allow us to build a drop-down of integration types.
export const capabilityToIntegrationConfigType = {
  spotByNetApp: SpotByNetAppIntegrationConfig,
}

export class Integration {
  constructor(
    private _integrationId: string,
    private _name: string,
    private _config: IntegrationConfig
  ) {}

  get integrationId() {
    return this._integrationId
  }

  get name() {
    return this._name
  }

  get config() {
    return this._config
  }
}

function integrationDtoToIntegration(dto: IntegrationDto) {
  let config: IntegrationConfig = null
  switch (dto.config.type) {
    case SpotByNetAppIntegrationConfig.integrationType:
      let dtoConfig: SpotByNetAppConfigDto = dto.config as SpotByNetAppConfigDto
      config = new SpotByNetAppIntegrationConfig(
        dtoConfig.sourceAccountId,
        dtoConfig.sourceAuthToken,
        dtoConfig.targetAccountId,
        dtoConfig.targetAuthToken
      )
      break
    default:
      throw Error(`Unrecognized Integration Type: ${dto.config.type}`)
  }

  return new Integration(dto.integrationId, dto.name, config)
}

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private accountIdSource = new ReplaySubject<string>(1)
  public accountIdStream = this.accountIdSource.asObservable()
  private accountDtoSource = new ReplaySubject<AccountDto>(1)
  public accountDtoStream = this.accountDtoSource.asObservable()
  private defaultIdpSource = new ReplaySubject<IdentityProviderDto>(1)
  public defaultIdpStream = this.defaultIdpSource.asObservable()
  private idpListSource = new ReplaySubject<IdentityProviderDto[]>(1)
  public idpListStream = this.idpListSource.asObservable()

  private accountDto: AccountDto = null
  private entitlementDto: EntitlementDto = null
  private entitlerDto: EntitlerDto = null
  public identityProviders: Map<string, IdentityProviderDto> = new Map()
  private integrationDtos: Array<IntegrationDto> = null

  constructor(private apiService: ApiService) {}

  loadAccount$(accountId: string) {
    return forkJoin([
      this.apiService.getAccount(accountId),
      this.apiService.getEntitlement(accountId),
      this.apiService.getEntitler(accountId),
      this.apiService.listIdentityProviders(accountId),
      this.apiService.listIntegrations(accountId),
    ]).pipe(
      map(([accountDto, entitlementDto, entitlerDto, identityProvidersDto, integrations]) => {
        this.accountDto = accountDto
        this.entitlementDto = entitlementDto
        this.entitlerDto = entitlerDto
        this.identityProviders = new Map(identityProvidersDto.map((i) => [i.identityProviderId, i]))
        this.integrationDtos = integrations

        this.idpListSource.next(identityProvidersDto)
        this.defaultIdpSource.next(this.getDefaultIdp(identityProvidersDto))
        this.accountDtoSource.next(this.accountDto)
        this.accountIdSource.next(this.accountDto.accountId)

        return accountDto
      })
    )
  }

  listIdentityProviders(includeArpio: boolean): IdentityProviderDto[] {
    if (this.identityProviders) {
      let nativeIdp: IdentityProviderDto = null
      const idpList = Array.from(this.identityProviders.values()).filter((idp) => {
        if (idp.type === 'native') {
          nativeIdp = idp
          return false
        } else {
          return true
        }
      })
      // Adding the native at the end of the list so e.g. selectors don't default to it.
      if (includeArpio && nativeIdp) {
        idpList.push(nativeIdp)
      }
      return idpList
    } else {
      return []
    }
  }

  /**
   * Lists identity providers available for SSO user management.
   * Filters out MS Entra IdPs that are not configured to be used for authentication
   */
  listSsoIdentityProviders(): IdentityProviderDto[] {
    return this.listIdentityProviders(true).filter((idp) => {
      if (idp.type === 'msEntra' && !idp.config.useForAuthentication) {
        // Filter out MS Entra IdPs that are not used for authentication
        return false
      }
      return true
    })
  }

  get accountId() {
    return this.accountDto && this.accountDto.accountId
  }

  hasNonNativeIdentityProvider(): boolean {
    return this.ssoEnabled() && Array.from(this.identityProviders.values()).some((idp) => idp.type !== 'native')
  }

  getIdentityProvider(id: string): IdentityProviderDto {
    return this.identityProviders.get(id)
  }

  addIdentityProvider(idp: IdentityProviderDto): void {
    this.identityProviders.set(idp.identityProviderId, idp)
    this.idpListSource.next(Array.from(this.identityProviders.values()))
  }

  removeIdentityProvider(id: string): void {
    this.identityProviders.delete(id)
    this.idpListSource.next(Array.from(this.identityProviders.values()))
  }

  get integrations() {
    return this.integrationDtos !== null ? this.integrationDtos.map(integrationDtoToIntegration) : null
  }

  saveIntegration$(integration: Integration) {
    let saveObservable = this.apiService.createIntegration(this.accountId, integration.name, integration.config.toDto())
    if (!!integration.integrationId) {
      // we're updating an existing integration.  Override the observable we just created
      saveObservable = this.apiService.updateIntegration(
        this.accountId,
        integration.integrationId,
        integration.name,
        integration.config.toDto()
      )
    }

    return saveObservable.pipe(
      tap((dto) => {
        this.integrationDtos = this.integrationDtos.filter(
          (existingDto) => existingDto.integrationId != dto.integrationId
        )
        this.integrationDtos.push(dto)
      })
    )
  }

  deleteIntegration$(integrationId: string) {
    return this.apiService
      .deleteIntegration(this.accountId, integrationId)
      .pipe(
        tap((_) => (this.integrationDtos = this.integrationDtos.filter((dto) => dto.integrationId !== integrationId)))
      )
  }

  updateAccountName$(accountName: string) {
    return this.apiService.updateAccount(this.accountId, accountName).pipe(
      tap((accountDto) => {
        this.accountDto = accountDto
        this.accountDtoSource.next(this.accountDto)
      })
    )
  }

  get entitlementLoaded() {
    return !!this.entitlementDto
  }

  get subscriptionExpiration(): Date {
    if (!this.entitlementDto) {
      // if the entitlementDto has not yet loaded, return a date 100 years out
      // this entitles the user by default, and shows an obviously incorrect
      // date in the UI when they look at the expiration.  This should only be
      // a brief situation while we're still loading the entitlement info.
      return new Date(Date.now() + (100 * 365 + 24) * 24 * 60 * 60 * 1000) // 100 years, including 24 leap days
    }
    return this.entitlementDto.expiration
  }

  get subscriptionExpired(): boolean {
    return this.subscriptionExpiration < new Date()
  }

  get productEdition(): string {
    if (!this.entitlementDto || !this.entitlementDto.productName) {
      return '... loading'
    }
    return this.entitlementDto.productName
  }

  get entitledBillableResourceCount() {
    return this.entitlementDto && this.entitlementDto.licensedBillableResourceCount
  }

  get currentBillableResourceCount() {
    return this.entitlementDto && this.entitlementDto.currentBillableResourceCount
  }

  entitledForCapability(capability: string): boolean {
    // If entitlements haven't been loaded yet (new account flow), default to enabled
    // This allows the UI to function properly during account creation before entitlements exist
    if (!this.entitlementDto) {
      return true
    }

    // Check if subscription is active and capability is included in entitlements
    return !this.subscriptionExpired && this.entitlementDto.capabilities.includes(capability)
  }

  /**
   * Admin-configurable flag that activates graduated enforcement of the contracted billable resource count
   * (disable apps at 106% of entitled count, re-enable at 104%). For effective enforcement state that also
   * accounts for the `NO_OVERAGE` marketplace dimension, use `overageEnforcementEnabled`.
   */
  get enforceLimits(): boolean {
    return this.entitlementDto?.enforceLimits ?? false
  }

  /**
   * True when the customer's marketplace subscription includes the `NO_OVERAGE` dimension, which caps usage
   * instead of billing for overage. When set, enforcement is active regardless of `enforceLimits` and Arpio
   * suppresses overage usage reporting to AWS Marketplace. For effective enforcement state that also
   * accounts for the `NO_OVERAGE` marketplace dimension, use `overageEnforcementEnabled`.
   */
  get noOverage(): boolean {
    return this.entitlementDto?.noOverage ?? false
  }

  /**
   * Effective enforcement state for the contracted billable resource count. True when either the
   * admin-configurable `enforceLimits` flag is on, or the marketplace subscription includes the
   * `NO_OVERAGE` dimension (which caps usage instead of billing for overage). When true, apps that
   * push the account total above the entitled count get disabled per the graduated 106%/104% thresholds.
   */
  get overageEnforcementEnabled(): boolean {
    return this.enforceLimits || this.noOverage
  }

  get marketplaceUrl(): string {
    return this.entitlerDto && this.entitlerDto.marketplaceUrl
  }

  get retentionSettings$() {
    return this.apiService
      .getRetentionSettings(this.accountId)
      .pipe(
        map((settingsDto) => [
          settingsDto.recentRetained,
          settingsDto.daysRetained,
          settingsDto.weeksRetained,
          settingsDto.monthsRetained,
          settingsDto.quartersRetained,
        ])
      )
  }

  setRetentionSettings$(
    recentRetained: number,
    daysRetained: number,
    weeksRetained: number,
    monthsRetained: number,
    quartersRetained: number
  ) {
    return this.apiService.setRetentionSettings(
      this.accountId,
      recentRetained,
      daysRetained,
      weeksRetained,
      monthsRetained,
      quartersRetained
    )
  }

  get resourceAttributes$() {
    return this.apiService.getResourceAttributes(this.accountId)
  }

  setResourceAttributes$(tags: { [key: string]: string }) {
    return this.apiService.setResourceAttributes(this.accountId, tags)
  }

  get resourceTypeConfigs$() {
    return this.apiService.getResourceTypeConfigs(this.accountId)
  }

  setResourceTypeConfigs$(resourceTypeConfigs: AccountResourceTypeConfigsDto) {
    return this.apiService.setResourceTypeConfigs(this.accountId, resourceTypeConfigs)
  }

  createSamlIdentityProvider$(
    name: string,
    metadataXml: string,
    useGlobalSpEntityId: boolean,
    autoCreateSubjects: boolean
  ) {
    const config = {
      type: 'saml',
      metadata: metadataXml,
      useGlobalSpEntityId: useGlobalSpEntityId,
      autoCreateSubjects: autoCreateSubjects,
    } as IdentityProviderConfigDto
    return this.apiService.createIdentityProvider(this.accountId, 'saml', name, config)
  }

  updateSamlIdentityProvider$(
    id: string,
    name: string,
    newMetadata: string,
    newUseGlobalSpEntityId: boolean,
    autoCreateSubjects: boolean
  ) {
    const config = {
      type: 'saml',
      metadata: newMetadata,
      useGlobalSpEntityId: newUseGlobalSpEntityId,
      autoCreateSubjects: autoCreateSubjects,
    } as IdentityProviderConfigDto
    return this.apiService.updateIdentityProvider(this.accountId, 'saml', name, id, config)
  }

  createMsEntraIdentityProvider$(
    name: string,
    tenantId: string,
    useForAuthentication: boolean,
    autoCreateSubjects: boolean
  ) {
    const config = {
      type: 'msEntra',
      tenantId: tenantId,
      useForAuthentication: useForAuthentication,
      autoCreateSubjects: autoCreateSubjects,
    } as IdentityProviderConfigDto
    return this.apiService.createIdentityProvider(this.accountId, 'msEntra', name, config)
  }

  updateMsEntraIdentityProvider$(
    id: string,
    name: string,
    tenantId: string,
    useForAuthentication: boolean,
    autoCreateSubjects: boolean
  ) {
    const config = {
      type: 'msEntra',
      tenantId: tenantId,
      useForAuthentication: useForAuthentication,
      autoCreateSubjects: autoCreateSubjects,
    } as IdentityProviderConfigDto
    return this.apiService.updateIdentityProvider(this.accountId, 'msEntra', name, id, config)
  }

  deleteIdentityProvider$(id) {
    return this.apiService.deleteIdentityProvider(this.accountId, id)
  }

  ssoEnabled() {
    return this.entitledForCapability('singleSignOn')
  }

  ssoNotEnabled() {
    return this.entitlementDto && !this.ssoEnabled()
  }

  awsEnabled() {
    // For AWS, default to true when entitlements haven't loaded yet
    // AWS is the legacy default, so this maintains backward compatibility
    if (!this.entitlementDto) {
      return true
    }
    return this.entitledForCapability('aws')
  }

  azureEnabled() {
    // For Azure, default to false when entitlements haven't loaded yet
    // This prevents showing the cloud provider selection step before we know
    // if the account actually has Azure capability
    if (!this.entitlementDto) {
      return false
    }
    return this.entitledForCapability('azure')
  }

  get stacksetsEnabled(): boolean | null {
    return this.accountDto?.stacksetsEnabled ?? null
  }

  get hasStacksetsChoice(): boolean {
    return this.accountDto?.stacksetsEnabled !== null && this.accountDto?.stacksetsEnabled !== undefined
  }

  get stacksetsCapabilityEnabled(): boolean {
    return this.entitledForCapability('stackSets')
  }

  realTimeRposEnabled() {
    return this.entitledForCapability('realTimeRpos')
  }

  get networkSandboxEnabled(): boolean {
    return this.entitledForCapability('networkSandbox')
  }
  get networkSandboxWhitelistEnabled(): boolean {
    return this.entitledForCapability('networkSandboxWhitelist')
  }

  get advancedNetworkSandboxEnabled(): boolean {
    return this.entitledForCapability('advancedNetworkSandbox')
  }

  get protectRecoveryPointEnabled(): boolean {
    return this.entitledForCapability('allowProtectRecoveryPoint')
  }

  get recoveryReportsEnabled(): boolean {
    return this.entitledForCapability('enableRecoveryReports')
  }

  get quarantineEnabled(): boolean {
    return this.entitledForCapability('quarantine')
  }

  private getDefaultIdp(idpDtos: IdentityProviderDto[]): IdentityProviderDto {
    if (idpDtos.length === 0) {
      return null
    }

    if (idpDtos.length === 1) {
      return idpDtos[0]
    }

    // Multiple providers: prioritize Arpio (native type) first, then alphabetical by name
    const arpioIdp = idpDtos.find((idp) => idp.type === 'native')
    if (arpioIdp) {
      return arpioIdp
    }

    // Default sort by name alphabetically
    return idpDtos.sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))[0]
  }

  get licensedForIntegrations() {
    let integrationCapabilities = ['spotByNetApp']
    return integrationCapabilities.some((capability) => this.entitledForCapability(capability))
  }

  get rbacEnabled(): boolean {
    return this.entitledForCapability('rbac')
  }

  get rbacFullEnabled(): boolean {
    return this.entitledForCapability('rbacFull')
  }

  get jobsTryAgainEnabled(): boolean {
    return this.entitledForCapability('jobsTryAgain')
  }
}
